import {
  type FrameType,
  RelativePositionTypeEnums,
  type TourStep,
} from '@carboncopyco/types';
import {
  INITIAL_OVERFLOW_AT_VALUES,
  TOUR_APPLY_TO_ALL_HEIGHT,
  TOUR_TOOLTIP_HEIGHT,
  TOUR_TOOLTIP_MARGIN,
  TOUR_TOOLTIP_WIDTH,
} from '../constants';
import {
  type AvailableSides,
  RelativePositionAlignmentEnums,
  type SideOverflowBooleans,
  type SideOverflowValues,
} from '../types';
import { getTargetElement } from './getTargetElement';

const ZERO_OVERFLOWS = {
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
};

const MIDDLE_CENTER = {
  relation: RelativePositionTypeEnums.page,
  alignment: RelativePositionAlignmentEnums.middleCenter,
};

const PLACEMENT_ORDER: AvailableSides[] = ['bottom', 'top', 'right', 'left'];
const PLACEMENT_SIDE_MAP: {
  [key in AvailableSides]: RelativePositionAlignmentEnums;
} = {
  top: RelativePositionAlignmentEnums.topCenter,
  right: RelativePositionAlignmentEnums.middleEnd,
  bottom: RelativePositionAlignmentEnums.bottomCenter,
  left: RelativePositionAlignmentEnums.middleStart,
};

const calculateOverflows = (element: HTMLElement) => {
  const { top, right, bottom, left } = element.getBoundingClientRect();
  const contentWindow = element.ownerDocument.defaultView;
  const { innerWidth, innerHeight } = contentWindow;

  return {
    top,
    right: innerWidth - right,
    bottom: innerHeight - bottom,
    left,
  };
};

export const calculateElementOverflows = (
  element: HTMLElement,
  doc: Document = document
): SideOverflowValues => {
  let totalOverflows = {
    ...ZERO_OVERFLOWS,
  };

  const getOverflows = (element: HTMLElement) => {
    if (!element) {
      return ZERO_OVERFLOWS;
    }

    const elementOwnerDocument = element.ownerDocument;
    const isElementInIframe = elementOwnerDocument !== doc;

    if (!isElementInIframe) {
      const { top, right, bottom, left } = calculateOverflows(element);
      return {
        top: totalOverflows.top + top,
        right: totalOverflows.right + right,
        bottom: totalOverflows.bottom + bottom,
        left: totalOverflows.left + left,
      };
    }

    const elementOwnerWindow = elementOwnerDocument.defaultView;
    const iframeElement = elementOwnerWindow.frameElement as HTMLIFrameElement;

    const calculatedOverflows = calculateOverflows(element);
    totalOverflows = {
      top: totalOverflows.top + calculatedOverflows.top,
      right: totalOverflows.right + calculatedOverflows.right,
      bottom: totalOverflows.bottom + calculatedOverflows.bottom,
      left: totalOverflows.left + calculatedOverflows.left,
    };
    return getOverflows(iframeElement);
  };

  return getOverflows(element);
};

export const willTooltipOverflow = (
  elementOverflows: SideOverflowValues,
  tooltipElement?: HTMLElement
): SideOverflowBooleans => {
  return Object.entries(elementOverflows).reduce((prev, next) => {
    const [location, availableSpace] = next;

    if (availableSpace <= 0) {
      return {
        ...prev,
        [location]: true,
      };
    }

    let tooltipHeight = TOUR_TOOLTIP_HEIGHT;
    let tooltipWidth = TOUR_TOOLTIP_WIDTH;

    if (tooltipElement) {
      const { width, height } = tooltipElement.getBoundingClientRect();
      tooltipHeight = height;
      tooltipWidth = width;
    }

    if (location === 'top' || location === 'bottom') {
      const combinedTooltipHeight =
        tooltipHeight + TOUR_TOOLTIP_MARGIN + TOUR_APPLY_TO_ALL_HEIGHT;
      return {
        ...prev,
        [location]: availableSpace < combinedTooltipHeight,
      };
    }

    if (location === 'left' || location === 'right') {
      const combinedTooltipWidth = tooltipWidth + TOUR_TOOLTIP_MARGIN;
      return {
        ...prev,
        [location]: availableSpace < combinedTooltipWidth,
      };
    }
  }, INITIAL_OVERFLOW_AT_VALUES);
};

export const pickInitialTooltipPlacement = ({
  xpath,
  elementPosition,
  frameType,
  doc = document,
}: {
  xpath?: TourStep['xpath'];
  elementPosition?: TourStep['elementPosition'];
  frameType: FrameType;
  doc?: Document;
}) => {
  const { targetElement } = getTargetElement({
    xpath,
    elementPosition,
    frameType,
    doc,
  });
  if (!targetElement) {
    return MIDDLE_CENTER;
  }

  if (targetElement.tagName.toUpperCase() === 'BODY') {
    return MIDDLE_CENTER;
  }

  const calculatedOverflows = calculateElementOverflows(targetElement, doc);
  const willOverflowAt = willTooltipOverflow(calculatedOverflows);
  const cannotPlaceAnywhere = Object.values(willOverflowAt).every(
    (willOverflowAt) => willOverflowAt
  );

  if (cannotPlaceAnywhere) {
    return MIDDLE_CENTER;
  }

  const pickedSide = PLACEMENT_ORDER.find((side) => !willOverflowAt[side]);
  if (!pickedSide) {
    return MIDDLE_CENTER;
  }

  return {
    relation: RelativePositionTypeEnums.element,
    alignment: PLACEMENT_SIDE_MAP[pickedSide],
  };
};
